import {
  CESS_RATE,
  DEFAULT_RISK_PROFILE,
  EQUITY_LTCG_EXEMPTION,
  EQUITY_LTCG_RATE,
  FIRE_MULTIPLE,
  GAINS_SURCHARGE_BANDS,
  INDIA_MARKET_MODEL,
  OTHER_LTCG_RATE,
  SLAB_BANDS,
} from '../assumptions/india'
import type { TaxBand } from '../assumptions/india'
import { RISKY_ASSETS } from '../engine/types'
import type { Allocation } from '../engine/types'

/**
 * What the FIRE number costs after tax.
 *
 * The product draws a corpus requirement of `FIRE_MULTIPLE` x inflated annual
 * expenses. That is a PRE-TAX number: a retiree does not spend a redemption,
 * they spend what is left once Indian capital-gains tax has taken the gain
 * embedded in it. This module computes the factor the requirement has to be
 * multiplied by so that what lands is what the plan says is needed.
 *
 * It sits deliberately OUTSIDE `src/engine/`. Nothing here runs per path, per
 * year, or per frame — the whole model collapses to one scalar handed to
 * `SimInputs.fireMultiple`, so the simulator, the worker protocol, the chart and
 * the share card are all untouched. That boundary is the reason this was
 * shippable as one cycle.
 *
 * ── Mix-blind, and that is a decision rather than a simplification ──────────
 *
 * The gross-up is computed from ONE FIXED REFERENCE ALLOCATION — the app's
 * default risk profile — and never from the user's own `profileId`. So the
 * tax-adjusted requirement moves with spending and horizon, and two scenarios
 * differing only by risk mix get the SAME number.
 *
 * That is not because allocation does not matter; it plainly does, and a
 * debt-heavy retiree's real drag is understated here. It is because making the
 * requirement vary by mix would have the app state that one risk mix needs less
 * money than another. `src/card/model.ts` already refused a milder version of
 * that juxtaposition, and whether it engages the second limb of SEBI's
 * definition of investment advice is an OPEN question in `docs/decisions.md`
 * that counsel settles. Satya chose mix-blind at Gate 2 planning rather than
 * ship the deeper exposure ahead of that answer.
 *
 * ── The tax, and what is deliberately not in it ─────────────────────────────
 *
 * Every rate, threshold and exemption read here is a `data` assumption in
 * `src/assumptions/india.ts` carrying a primary source, and the statutory
 * provenance is in `.claude/ledger/capital-gains-*-fy2026-27.md`. FY 2026-27
 * runs under the Income-tax Act, 2025: s.198 for long-term equity, s.197 for
 * other long-term gains, s.76 deeming post-April-2023 debt-fund units
 * short-term however long they were held, which is what sends the debt sleeve to
 * slab rates.
 *
 * Four approximations. An earlier version of this comment claimed all of them
 * "push the requirement UP, so the number is conservative rather than
 * flattering" — the review showed that is FALSE in two of the four, so the
 * direction of each is now stated individually and no blanket conservatism is
 * claimed anywhere, including in the footer:
 *
 *  1. **The s.87A rebate is not modelled** — pushes the requirement UP. It would
 *     reduce tax at low incomes and needs total-income modelling the app does not
 *     have, so a modest retiree's tax is overstated.
 *  2. **Cost basis of the opening corpus is unknown** — pushes it DOWN. The gain
 *     fraction is derived from the contribution stream and then applied to the
 *     whole corpus, but a lump present from year zero compounds for the full
 *     horizon and so carries a HIGHER gain fraction than the stream. On the
 *     default plan (₹50L corpus, 18 years) the stream figure is 0.577 where the
 *     blended truth is 0.649, understating the gross-up by about 0.85pp. The gap
 *     widens as the opening corpus grows, and is worst for someone with a large
 *     corpus and no monthly contribution at all.
 *  3. **Surcharge is capped at 15% across the whole tax** — pushes it DOWN at the
 *     extremes. Statutorily the 15% cap covers only the s.196/197/198 gains; the
 *     slab-taxed debt sleeve would attract the uncapped ladder. At ₹25 crore of
 *     realised gain this reads about 3.8% low. Reachable, but only at the very top
 *     of the expense slider.
 *  4. **The withdrawal is inflated at the deterministic mean** — no direction.
 *     The engine inflates stochastically per path, but `fireMultiple` is one
 *     scalar, so the gross-up is a point estimate rather than a distribution.
 *
 * Marginal relief on surcharge IS modelled, and is not a refinement — see the note
 * above `baseTax` for why leaving it out produced a two-rooted equation.
 *
 * ── The one that is worth knowing about ─────────────────────────────────────
 *
 * `EQUITY_LTCG_EXEMPTION` is a NOMINAL rupee threshold and the withdrawal is
 * INFLATED. So the exemption absorbs most of a small near-term withdrawal and
 * almost none of a large distant one, and the gross-up therefore grows with
 * horizon — roughly 1.006x at five years and 1.10x at thirty-five on a ₹12L
 * plan. The engine then applies that single target-age factor at EVERY year of
 * the projection (`simulate.ts`: `required = baseRequirement * cumInflation`),
 * which overstates the requirement early on. Pricing it per year means a
 * per-year multiple, which is an engine change, and that is F7b.
 */

/**
 * The mix the tax model is computed against, regardless of what the user picked.
 * Read the module header before changing this to the user's own allocation —
 * mix-blindness is a legal-exposure decision, not a default.
 */
export const REFERENCE_ALLOCATION: Allocation = DEFAULT_RISK_PROFILE.allocation

export interface WithdrawalTaxInputs {
  /** Annual spending the corpus must fund, in nominal rupees AT the target age. */
  annualWithdrawalAtTarget: number
  /** Years of accumulation — `fireByAge - currentAge`. */
  accumulationYears: number
  /**
   * Nominal annual increase in the contribution, as a decimal. Passed in rather
   * than imported: `plan.ts` owns `ANNUAL_STEP_UP` and imports this module, so
   * reading it from there would close an import cycle.
   */
  contributionStepUpPct: number
}

/**
 * Every statutory number this module reads, in one place.
 *
 * Threaded as a parameter rather than reached for directly, for two reasons. It
 * enumerates exactly what the model depends on, which is what Done-when 1 is
 * about — if a rate is not in here, the module does not read it. And it makes
 * "the gross-up is exactly 1.0 when every rate is zero" a property a test can
 * actually exercise, instead of one that needs the module graph mocked.
 */
export interface WithdrawalTaxRates {
  equityLtcg: number
  equityLtcgExemption: number
  otherLtcg: number
  cess: number
  slabBands: readonly TaxBand[]
  surchargeBands: readonly TaxBand[]
}

/** The rates actually in force, every one a sourced `data` assumption. */
export const STATUTORY_RATES: WithdrawalTaxRates = {
  equityLtcg: EQUITY_LTCG_RATE.value,
  equityLtcgExemption: EQUITY_LTCG_EXEMPTION.value,
  otherLtcg: OTHER_LTCG_RATE.value,
  cess: CESS_RATE.value,
  slabBands: SLAB_BANDS,
  surchargeBands: GAINS_SURCHARGE_BANDS,
}

/** Progressive tax: each band's rate applies only to the slice inside it. */
function bandedTax(amount: number, bands: readonly TaxBand[]): number {
  let tax = 0
  let floor = 0
  for (const band of bands) {
    if (amount > floor) tax += (Math.min(amount, band.upTo) - floor) * band.rate
    floor = band.upTo
    if (amount <= band.upTo) break
  }
  return tax
}

/**
 * Flat rate chosen by which band the amount falls in — NOT progressive.
 * Surcharge works this way: one rate applied to the whole of the computed tax.
 */
function rateForBand(amount: number, bands: readonly TaxBand[]): number {
  for (const band of bands) if (amount <= band.upTo) return band.rate
  return bands[bands.length - 1].rate
}

/**
 * Expected gross return on a mix, as a multiplier (1.09 = +9% a year).
 *
 * The risky sleeves' `mean` is the LOG of the expected gross return — see the
 * units note in `india.ts` — so it needs `exp`, not `1 +`. Getting that wrong
 * understates the blended return and with it the embedded gain. The cash sleeve
 * is a plain annual rate and is the exception.
 */
export function blendedGrossReturn(allocation: Allocation): number {
  let blended = allocation.cash * (1 + INDIA_MARKET_MODEL.cashReturn)
  for (const asset of RISKY_ASSETS) {
    blended += allocation[asset] * Math.exp(INDIA_MARKET_MODEL.assets[asset].mean)
  }
  return blended
}

/**
 * What fraction of the corpus at retirement is unrealised gain rather than
 * contributed capital.
 *
 * Derived, not assumed. A stream stepped up at `contributionStepUpPct` and
 * compounded at the mix's blended return gives contributions `C` and value `V`;
 * the gain fraction is `1 - C/V`. Both `C` and `V` are linear in the
 * contribution LEVEL, so this is invariant to how much the user saves — the
 * savings rate does not move the FIRE number, which would be incoherent. It
 * depends only on horizon and mix, and with the mix fixed, only on horizon.
 *
 * Runs from ~0.18 at five years to ~0.82 at thirty-five. Near zero for a short
 * horizon is correct: there has not been time to accumulate a gain to tax.
 */
export function embeddedGainFraction(
  accumulationYears: number,
  contributionStepUpPct: number,
  allocation: Allocation = REFERENCE_ALLOCATION,
): number {
  const years = Math.floor(accumulationYears)
  if (years <= 0) return 0

  const growth = blendedGrossReturn(allocation)
  let contributed = 0
  let value = 0
  for (let year = 1; year <= years; year++) {
    const instalment = Math.pow(1 + contributionStepUpPct, year - 1)
    contributed += instalment
    value += instalment * Math.pow(growth, years - year)
  }

  if (!(value > 0)) return 0
  return Math.max(0, 1 - contributed / value)
}

/**
 * Tax on a realised gain, split across the mix.
 *
 * Equity pays `EQUITY_LTCG_RATE` above the annual exemption; gold pays
 * `OTHER_LTCG_RATE` from the first rupee, having no exemption of its own; debt
 * pays slab rates because s.76 deems it short-term; cash realises no gain.
 * Surcharge then applies to the computed tax at the capped gains rate, and cess
 * on top of both — slab or flat rate, then surcharge, then cess, which is the
 * statutory order.
 */
export function taxOnRealisedGain(
  gain: number,
  allocation: Allocation = REFERENCE_ALLOCATION,
  rates: WithdrawalTaxRates = STATUTORY_RATES,
): number {
  if (!(gain > 0)) return 0

  const surchargeRate = rateForBand(gain, rates.surchargeBands)
  let payable = baseTax(gain, allocation, rates) * (1 + surchargeRate)

  // Marginal relief at every surcharge threshold the gain has crossed. See the
  // note below for why this is not optional.
  for (const band of rates.surchargeBands) {
    const threshold = band.upTo
    if (!Number.isFinite(threshold) || gain <= threshold) continue
    const atThreshold =
      baseTax(threshold, allocation, rates) *
      (1 + rateForBand(threshold, rates.surchargeBands))
    payable = Math.min(payable, atThreshold + (gain - threshold))
  }

  return payable * (1 + rates.cess)
}

/**
 * Tax before surcharge and cess: each sleeve at its own rate.
 *
 * Separated out because marginal relief has to ask what the tax would have been at
 * a threshold, and that cannot be got by scaling — the slab is progressive and the
 * equity exemption is a fixed rupee amount, so the base tax is not linear in the
 * gain.
 */
function baseTax(
  gain: number,
  allocation: Allocation,
  rates: WithdrawalTaxRates,
): number {
  const equityGain = gain * allocation.equity
  const debtGain = gain * allocation.debt
  const goldGain = gain * allocation.gold

  return (
    Math.max(0, equityGain - rates.equityLtcgExemption) * rates.equityLtcg +
    goldGain * rates.otherLtcg +
    bandedTax(debtGain, rates.slabBands)
  )
}

/*
 * ── Why marginal relief is load-bearing rather than a refinement ─────────────
 *
 * Surcharge is a cliff: a flat percentage of the WHOLE computed tax, switched on
 * by total income crossing a threshold. Without relief, crossing ₹50 lakh of gain
 * adds 10% of all the tax at once, so the amount a retiree NETS after tax jumps
 * DOWNWARD as the redemption grows. That makes `gross - tax(gross)` non-monotonic,
 * and a non-monotonic function does not have one root — it has two, and a
 * bisection returns whichever its bracket happens to straddle.
 *
 * The review measured exactly that on the first version of this module: at a
 * ₹65.5 lakh net withdrawal the equation had roots at ₹70.54 lakh and ₹71.11 lakh
 * and the solver returned the larger — 0.795% too much corpus, across a band of
 * spenders sitting inside the app's own sliders.
 *
 * The statute already solves it, which is the part worth noticing. Marginal relief
 * exists so that crossing a threshold can never cost more in tax than the income
 * which crossed it. Applying it is both more correct AND what restores the single
 * root. Omitting it was not a conservative simplification; it was the bug.
 */

/**
 * The factor the pre-tax requirement must be multiplied by.
 *
 * Solves `net = gross - tax(g * gross)` for `gross` and returns `gross / net`.
 * Bisection rather than algebra because the debt sleeve is progressive and the
 * equity exemption is a kink, so there is no closed form.
 *
 * **Deliberately unclamped.** The result being >= 1 is a property the tests
 * assert, not one this function imposes: a clamp would make a sign error or a
 * negative taxable amount invisible, which is exactly how an unfalsifiable
 * criterion gets ticked.
 */
export function withdrawalGrossUp(
  inputs: WithdrawalTaxInputs,
  rates: WithdrawalTaxRates = STATUTORY_RATES,
): number {
  const net = inputs.annualWithdrawalAtTarget
  if (!(net > 0)) return 1

  const gainFraction = embeddedGainFraction(
    inputs.accumulationYears,
    inputs.contributionStepUpPct,
  )
  if (gainFraction <= 0) return 1

  // No tax due at the net figure means the net figure IS the solution, so the
  // gross-up is exactly 1 rather than one bisection step away from it. Tax is
  // monotonic in the gain, so this is an exact short-circuit and not a rounding
  // convenience: Done-when 2 asks for exactly 1.0 at zero rates, and a search
  // that converges to 1 + 2e-16 does not deliver it.
  if (taxOnRealisedGain(gainFraction * net, REFERENCE_ALLOCATION, rates) === 0) return 1

  // Tax never exceeds ~34% of the gain and the gain never exceeds the
  // redemption, so the root sits well inside this bracket.
  //
  // The LOW end is deliberately below `net`. Starting the search at `net` would
  // make "the gross-up is never below 1" true by construction — the interval
  // could not represent a smaller answer — which is the same unfalsifiable
  // guarantee a Math.max would give, just hidden in the search instead of the
  // return. With room underneath, a sign error or a negative taxable amount
  // produces a gross-up below 1 and the test for it can actually fail.
  let low = net * 0.25
  let high = net * 4
  for (let i = 0; i < 200; i++) {
    const gross = (low + high) / 2
    if (gross - taxOnRealisedGain(gainFraction * gross, REFERENCE_ALLOCATION, rates) < net) {
      low = gross
    } else {
      high = gross
    }
  }

  return (low + high) / 2 / net
}

/**
 * `FIRE_MULTIPLE` with the withdrawal tax priced in. This is what the engine
 * consumes; `FIRE_MULTIPLE` itself stays 30 and stays the published norm, so the
 * F3 panel goes on quoting the withdrawal rule rather than this derived figure.
 */
export function taxAdjustedFireMultiple(
  inputs: WithdrawalTaxInputs,
  rates: WithdrawalTaxRates = STATUTORY_RATES,
): number {
  return FIRE_MULTIPLE.value * withdrawalGrossUp(inputs, rates)
}
